import { useCallback, useContext, useState } from 'react'
import Router, { useRouter } from 'next/router'
import Link from 'next/link'
import { destroyCookie } from 'nookies'
import { signOut } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { addLocalePrefix, getLangLinkProps } from 'lib/addLocalePrefix'
import useCheckBreakpoint from 'lib/checkBreakpoint'
import { getUser } from 'state/ducks/user/selectors'
import { StoreState } from 'state/ducks'
import { AuthContext } from 'providers/AuthProvider'
import eventEmitter from 'lib/eventEmitter'

//type
import { Lang } from 'types/lang'

//hooks
import { getLangs, getLangByLocale } from 'hooks/common/lang'
import { removeSpaceSearchParams } from 'lib/removeSpaceSearchParams'

import styles from './SideMenuComponent.module.scss'

interface Props {}

const SideMenuComponent: React.FC<Props> = (Props) => {
  const user = useSelector((state: StoreState) => getUser(state))
  const [isOpenAccordion, setIsOpenAccordion] = useState<boolean>(false)
  const { isSigned, userId } = useContext(AuthContext)
  const { asPath, locale } = useRouter()
  const { isMobile } = useCheckBreakpoint()
  const { t } = useTranslation()

  const _onPressLogout = useCallback((): void => {
    destroyCookie(null, 'jwt_token', { path: '/' })
    destroyCookie(null, 'user_id', { path: '/' })
    // @ts-ignore
    signOut({ redirect: false, callbackUrl: '/' })
    removeSpaceSearchParams()
    Router.push('/')
  }, [])

  const langs: Lang[] = getLangs().filter((lang: Lang) => {
    return lang.slug !== locale
  })

  const getDefaultDateTime = useCallback((type: string) => {
    let dateTime: string = ''

    if (type === 'start') {
      const now = moment()

      if (now.get('minutes') < 30) {
        now.set('minutes', 30)
      } else {
        now.set({ hours: now.get('hours') + 1, minutes: 0 })
      }

      dateTime = `${now.get('hours').toString().padStart(2, '0')}:${now
        .get('minutes')
        .toString()
        .padStart(2, '0')}`
    } else {
      const now = moment()

      if (now.get('minutes') < 30) {
        now.set({ hours: now.get('hours') + 2, minutes: 30 })
      } else {
        now.set({ hours: now.get('hours') + 3, minutes: 0 })
      }

      dateTime = `${now.get('hours').toString().padStart(2, '0')}:${now
        .get('minutes')
        .toString()
        .padStart(2, '0')}`
    }
    return dateTime
  }, [])

  const handleSearchLocation = useCallback(() => {
    let path = '/locations'

    if (Router.pathname === path) return

    const query = new URLSearchParams()

    const startDateTime = getDefaultDateTime('start')
    const endDateTime = getDefaultDateTime('end')

    query.append('startDate', moment().format('YYYY-MM-DD'))
    query.append('endDate', moment().format('YYYY-MM-DD'))
    query.append('startDateTimeHour', startDateTime.substring(0, startDateTime.indexOf(':')))
    query.append('startDateTimeMin', startDateTime.substring(startDateTime.indexOf(':') + 1))
    query.append('endDateTimeHour', endDateTime.substring(0, endDateTime.indexOf(':')))
    query.append('endDateTimeMin', endDateTime.substring(endDateTime.indexOf(':') + 1))
    query.append('bagSize', '0')
    query.append('suitcaseSize', '1')
    query.append('lat', '35.681236')
    query.append('lon', '139.767125')
    query.append('isLocation', 'false')

    Router.push(`${path}?${query.toString()}`)
  }, [getDefaultDateTime])

  return (
    <div className={styles.sideMenu}>
      <nav>
        <ul className={styles.menu}>
          <li>
            <a onClick={() => handleSearchLocation()}>
              <img src="/search_icon.svg" alt={t('META.SEARCH_ICON')} />
              {t('COMMON.SIDE_MENU.SEARCH')}
            </a>
          </li>
          {isSigned && (
            <>
              <li>
                <Link href={addLocalePrefix('/bookings?tab=current', locale)}>
                  <a>
                    <img src="/booking_icon.svg" alt={t('META.BOOKING_ICON')} />
                    {t('COMMON.SIDE_MENU.BOOKING_LIST')}
                  </a>
                </Link>
              </li>
              <li>
                <Link href={addLocalePrefix(`/users/${userId}`, locale)}>
                  <a>
                    <img src="/setting_icon.svg" alt={t('META.SETTING_ICON')} />
                    {t('COMMON.SIDE_MENU.ACCOUNT')}
                    {user && !user?.phone_verified && (
                      <img src="/warning.png" alt="" className={styles.warningIcon} />
                    )}
                  </a>
                </Link>
              </li>
            </>
          )}
          <li>
            <Link href="https://intercom.help/ecbocloak" passHref>
              <a target="_blank" rel="noreferrer">
                <img src="/faq_icon.svg" alt={t('META.FAQ_ICON')} />
                {t('COMMON.SIDE_MENU.QUESTION')}
              </a>
            </Link>
          </li>
          <li className={styles.openIntercom} onClick={() => window.Intercom('showMessages')}>
            <img src="/contact_icon.svg" alt={t('META.CONTACT_ICON')} />
            {t('COMMON.SIDE_MENU.CONTACT')}
          </li>
          {isMobile && (
            <>
              <li
                className={styles.accordionMenu}
                onClick={() => setIsOpenAccordion(!isOpenAccordion)}
              >
                <img
                  className={styles.accordionMenuLangIcon}
                  src="/language_icon.svg"
                  alt={t('META.LANGUAGE_ICON')}
                  height={24}
                  width={24}
                />
                <div className={styles.accordionMenuTitle}>{getLangByLocale(locale).name}</div>
                <img
                  className={`${styles.accordionMenuArrowIcon} ${
                    isOpenAccordion && styles.accordionMenuArrowIconActive
                  }`}
                  src="/arrow_bottom_black_02.svg"
                  alt={t('META.LANGUAGE_ICON')}
                  width={24}
                  height={24}
                />
              </li>
              {isOpenAccordion && (
                <ul className={styles.subMenu}>
                  {langs.map((lang: Lang, index) => (
                    <li key={index} onClick={() => localStorage.setItem('language', lang.slug)}>
                      <Link {...getLangLinkProps(lang.slug, asPath)} passHref>
                        <a className={styles.language}>{lang.name}</a>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </ul>
        {isMobile && (
          <>
            <ul className={`${styles.menu} ${styles.menuPartner}`}>
              <li>
                <Link href={`${process.env.AUTH_API_URL}/owners/sign_in`}>
                  <a>
                    <img src="/owner_icon_black.svg" alt={t('META.PARTNER_ICON')} />
                    {t('COMMON.SIDE_MENU.PARTNER')}
                    <img
                      className={styles.menuArrowIcon}
                      src="/arrow_bottom_black_02.svg"
                      alt={t('META.ARROW_ICON')}
                      width={24}
                      height={24}
                    />
                  </a>
                </Link>
              </li>
            </ul>
            <ul className={styles.buttonMenuList}>
              {isSigned ? (
                <li className={styles.buttonMenuListItem}>
                  <button className={styles.buttonMenu} type="button" onClick={_onPressLogout}>
                    {t('COMMON.HEADER.LOGOUT')}
                  </button>
                </li>
              ) : (
                <>
                  <li
                    onClick={() => eventEmitter.emit('redirectToAuth')}
                    className={styles.buttonMenuListItem}
                  >
                    <Link href={addLocalePrefix('/users/sign_up', locale)}>
                      <a className={`${styles.buttonMenu} ${styles.buttonMenuActive}`}>
                        {t('COMMON.HEADER.REGISTER')}
                      </a>
                    </Link>
                  </li>
                  <li
                    onClick={() => eventEmitter.emit('redirectToAuth')}
                    className={styles.buttonMenuListItem}
                  >
                    <Link href={addLocalePrefix('/users/sign_in', locale)}>
                      <a className={styles.buttonMenu}>{t('COMMON.HEADER.LOGIN')}</a>
                    </Link>
                  </li>
                  <li className={styles.buttonMenuListItem}>
                    <Link href="https://join-cloak.ecbo.io/owner">
                      <a className={styles.buttonMenu}>{t('TOP.OWNER.REGISTER')}</a>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </>
        )}
      </nav>
      {!isMobile && (
        <>
          <div className={styles.ownerMenu}>
            <Link href={`${process.env.AUTH_API_URL}/owners/sign_in`}>
              {t('COMMON.SIDE_MENU.PARTNER')}
            </Link>
          </div>
          {isSigned && (
            <div className={styles.logout}>
              <button className={styles.logoutButton} type="button" onClick={_onPressLogout}>
                {t('COMMON.HEADER.LOGOUT')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SideMenuComponent
